import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import {
  useTable,
  useResizeColumns,
  useFlexLayout,
  Column,
  useExpanded,
  useSortBy,
  useRowSelect,
  useFilters,
  useColumnOrder,
} from 'react-table';
import { TableRow, Typography, TableSortLabel, Box, CircularProgress, Tooltip } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { VariableSizeList as List, VariableSizeList } from 'react-window';
import Skeleton from '@mui/material/Skeleton';
import InfiniteLoader from 'react-window-infinite-loader';
import useStyles from './styles';
import { throttle } from '../../globals/utils';
import { useMountEffect } from '../../globals/hooks/use-mount-effect';
import { filterList } from './components/filter/filter-list';
import useUpdateEffect from '../../globals/hooks/use-update-effect';

type Order = 'asc' | 'desc';

const itemStatusMap: { [key: number]: boolean } = {};
let expandedHeights: { [key: string]: number } = {};
let previousStopIndex = 0;
const isItemLoaded = (index: number) => !!itemStatusMap[index];
const firstRowNumber = (object: { [key: string]: any }) =>
  Number.parseInt(Object.keys(object)[0], 10) - 1 || 0;

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }: any, reference: any) => {
    const defaultReference = React.useRef();
    const resolvedReference = reference || defaultReference;
    React.useEffect(() => {
      resolvedReference.current.indeterminate = indeterminate;
    }, [resolvedReference, indeterminate]);

    return (
      <>
        <input type="checkbox" ref={resolvedReference} {...rest} />
      </>
    );
  },
);

const VirtualizedTable = ({
  columns,
  data,
  useFrontEndSorting,
  initialSortDirection,
  initialSort,
  tableContainer,
  itemCount,
  loadMoreItems,
  columnOrder,
  columnVisibility,
  onSortChange,
  onColumnWidthsChange,
  onRowSelection,
  sortableAccessors,
  renderRowSubComponent,
  loading,
  cellHeight,
  tableHeight,
  chunkSize,
  subcomponentExpansionFlag,
  errorFlag,
  sortByString,
  autoReset,
  headerCheckbox,
}: {
  columns: Column<object>[];
  data: any[];
  useFrontEndSorting?: boolean;
  initialSortDirection?: Order;
  initialSort?: string;
  tableContainer: React.RefObject<HTMLElement>;
  itemCount: number;
  loadMoreItems?: Function;
  columnOrder?: string[];
  columnVisibility?: string[];
  onSortChange?: Function;
  onColumnWidthsChange?: Function;
  onRowSelection?: Function;
  sortableAccessors: { [key: string]: boolean };
  renderRowSubComponent?: FC<any>;
  loading?: boolean;
  cellHeight?: number;
  tableHeight?: number;
  chunkSize?: number;
  subcomponentExpansionFlag?: boolean | false;
  errorFlag?: boolean;
  sortByString?: any;
  autoReset?: boolean;
  headerCheckbox?:boolean;
}) => {
  const defaultColumn = useMemo(
    () => ({
      minWidth: 0,
      width: (tableContainer?.current?.offsetWidth || 700) / columns.length,
      maxWidth: 200,
    }),
    [columns.length, tableContainer],
  );

  const filterTypes = React.useMemo(() => filterList, []);
  const {
    getTableProps,
    getTableBodyProps,
    headers,
    rows,
    prepareRow,
    state,
    visibleColumns,
    setColumnOrder,
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      autoResetExpanded: autoReset,
      initialState: {
        hiddenColumns: columnVisibility || [],
      },
    },
    useResizeColumns,
    useFlexLayout,
    useFilters,
    useSortBy,
    useExpanded,
    useRowSelect,
    useColumnOrder,
    useRowSelect,
    (hooks) => {
      if (onRowSelection)
        hooks.visibleColumns.push((columnsPre: Column<object>[]) => [
          {
            id: 'selection',
            width: '47',
            disableResizing: true,
            Header: ({
              getToggleAllRowsSelectedProps,
            }
            : {
              getToggleAllRowsSelectedProps: Function;
            }) => (
             <td className="alignCenter">
                {headerCheckbox === false ? "" : <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />}
              </td>
            ),
            Cell: ({ row }: { row: any }) => (
              <td className="alignCenter">
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </td>
            ),
          },
          ...columnsPre,
        ]);
    },
  );

  const [order, setOrder] = useState<Order>(initialSortDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string | undefined>(initialSort);
  const [dimensions, setDimensions] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({ width: undefined, height: undefined });
  const tableState: any = state;
  const listReference = useRef<VariableSizeList>(null);

  const reRenderAfterRow = (rowNumber: number) => {
    listReference?.current?.resetAfterIndex(rowNumber);
  };
  useEffect(() => {
    if (sortByString === null) {
      setOrderBy('gatewayReceiptTime');
      setOrder('desc');
    }
  }, [
    sortByString]);
 
  const loadMoreTracker = (_: any, stopIndex: number) => {
    if (previousStopIndex < stopIndex && chunkSize && !subcomponentExpansionFlag) {
      previousStopIndex = stopIndex;
      const targetNumberOfRecords = chunkSize + Math.ceil(stopIndex / 100) * chunkSize;
      if (rows.length - 1 < targetNumberOfRecords && loadMoreItems) {
        return loadMoreItems(itemCount > targetNumberOfRecords ? targetNumberOfRecords : itemCount);
      }
    }
    return null;
  };

  const getSize = (index: number) => {
    if (tableState.expanded[`${index}`]) {
      return (expandedHeights && expandedHeights[`${index}`] + 80) || 300;
    }
    return cellHeight || 30;
  };

  const updateTableState = () => {
    const widths = tableState.columnResizing.columnWidths;
    if (Object.keys(widths).length !== 0 && onColumnWidthsChange) onColumnWidthsChange(widths);
    reRenderAfterRow(0);
    setDimensions({
      width: tableContainer?.current?.offsetWidth,
      height: tableContainer?.current?.offsetHeight,
    });
    return null;
  };

  const throttleUpdateColumns = () => {
    throttle(updateTableState, tableContainer, 200);
  };
  useMountEffect(() => {
    window.addEventListener('resize', throttleUpdateColumns);
    return () => {
      window.removeEventListener('resize', throttleUpdateColumns);
    };
  });

  useEffect(() => {
    reRenderAfterRow(firstRowNumber(tableState.expanded) || 0);
  }, [tableState.expanded]);

  useUpdateEffect(() => {
    updateTableState();
  }, [tableState.columnResizing.columnWidths]);

  const setColumns = () => {
    if (columnOrder) setColumnOrder(columnOrder);
    if (columnVisibility) setHiddenColumns(columnVisibility);
  };
  useEffect(() => {
    setColumns();
  }, [columnOrder, columnVisibility]);

  useEffect(() => {
    if (onRowSelection) onRowSelection(tableState.selectedRowIds);
  }, [tableState.selectedRowIds]);

  useEffect(() => {
    previousStopIndex = 0;
    reRenderAfterRow(0);
  }, [rows]);

  const handleExpandedHeightsChange = (heights: { [key: string]: number }) => {
    expandedHeights = { ...expandedHeights, ...heights };
    reRenderAfterRow(firstRowNumber(heights));
  };

  const handleRequestSort = (accessor: string, toggleCallback?: Function) => {
    const orderToSet = order === 'asc' ? 'desc' : 'asc';
    setOrder(orderBy !== accessor ? (initialSortDirection || 'desc') : orderToSet);
    setOrderBy(accessor);
    if (onSortChange && !useFrontEndSorting)
      onSortChange({
        columnName: accessor,
        direction: orderToSet,
      });
    else if (toggleCallback) toggleCallback(order === 'desc', false);
  };
  const renderCell = (row: any, cell: any) => {
    const column: any = columns.find((row) => row.Header === cell.column.Header);
    if (column && column.component) {
      // Custom component cell, do not wrap in tooltip
      return <cell.column.component row={row} />;
    }
    const { value } = cell;
    const cellReference = React.useRef<HTMLSpanElement>(null);
    const [showTooltip, setShowTooltip] = React.useState(false);

    // Only check for overflow on mouse enter
    const handleMouseEnter = () => {
      const element = cellReference.current;
      if (element && value !== undefined && value !== null && value !== '') {
        setShowTooltip(element.scrollWidth > element.clientWidth);
      } else {
        setShowTooltip(false);
      }
    };
    const handleMouseLeave = () => {
      setShowTooltip(false);
    };

    return (
      <Tooltip
        title={showTooltip && value !== undefined && value !== null && value !== '' ? String(value) : ''}
        arrow
        placement="top"
        open={showTooltip}
        disableHoverListener
      >
        <span
          ref={cellReference}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {cell.render('Cell')}
        </span>
      </Tooltip>
    );
  };
  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index];
      prepareRow(row);
      return (
        <div
          {...row.getRowProps({
            style,
          })}
          className="containerRow"
        >
          <TableRow
            {...row.getRowProps()}
            className="tr"
            id={`row-${row.id}`}
            style={{ height: cellHeight ? `${cellHeight}px` : '30px' }}
          >
            {row.cells.map((cell: any) => (
              <div {...cell.getCellProps()} className="td">
                {cell.column.component ? (
                  <cell.column.component row={row} />
                ) : (
                  renderCell(row, cell)
                )}
              </div>
            ))}
          </TableRow>
          {renderRowSubComponent && row.isExpanded && (
            <div className="detailsDropDown">
              {renderRowSubComponent({
                row: row.original,
                id: row.id,
                handleExpandedHeightsChange,
              })}
            </div>
          )}
        </div>
      );
    },
    [prepareRow, rows, cellHeight, visibleColumns.length, renderRowSubComponent],
  );
  const RenderInfiniteRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index];
      const isLoaded = row;
      itemStatusMap[index] = true;
      if (isLoaded) {
        prepareRow(row);
        itemStatusMap[index] = true;
      } else itemStatusMap[index] = false;

      const rowProperties = isLoaded
        ? {
          ...row.getRowProps({
            style,
          }),
        }
        : {};
      return (
        <div {...rowProperties} className="containerRow">
          <div className="tr" id={`row-${row?.id}`}>
            {isLoaded ? (
              row.cells.map((cell) => (
                <div style={style} {...cell.getCellProps()} className="td">
                  {renderCell(row, cell)}
                </div>
              ))
            ) : errorFlag ? null
              :
              (
                <td style={style} className="td">
                  loading...
                </td>
              )}
          </div>
          {isLoaded && renderRowSubComponent && row.isExpanded && (
            <div className="detailsDropDown">
              {renderRowSubComponent({
                row: row.original,
                id: row.id,
                handleExpandedHeightsChange,
              })}
            </div>
          )}
        </div>
      );
    },
    [prepareRow, rows, visibleColumns.length, renderRowSubComponent],
  );

  const classes = useStyles();
  const height = tableHeight || dimensions.height || tableContainer?.current?.offsetHeight || 0;
  const heightAfterHead = height - 83;
  const width = dimensions.width || tableContainer?.current?.offsetWidth || 0;
  const ruleAdjustedWidth = width - 2;

  return (
    <div className={classes.root}>
      <div {...getTableProps()} className="thead">
        <div className="tr">
          {headers.map((column: any) => {
            const headerProperties = column.getHeaderProps();
            const accessor = headerProperties.key.replace('header_', '');
            return (
              column.isVisible && (
                <div {...headerProperties} className="headCell">
                  <Typography variant="h6" className="headerText">
                    {column.render('Header')}
                  </Typography>
                  <span>{column.filter ? column.render('Filter') : null}</span>
                  {sortableAccessors[accessor] && (
                    <span>
                      <TableSortLabel
                        id={`sort-${column.id}`}
                        active={orderBy === column.id}
                        onClick={() => {
                          handleRequestSort(column.id, column.toggleSortBy);
                        }}
                        direction={orderBy === column.id ? order : 'asc'}
                        IconComponent={ExpandMore}
                        hideSortIcon={false}
                      />
                    </span>
                  )}
                  {column.getResizerProps && (
                    <span {...column.getResizerProps()} className="resizer" />
                  )}
                </div>
              )
            );
          })}
        </div>
      </div>
      {loading && (
        <Box className={classes.spinnerHolder} height={heightAfterHead} width={width}>
          <CircularProgress color="secondary" size={60} thickness={2} />
        </Box>
      )}
      {rows.length >= 1 ? (
        <div className="tbody" id="table-body">
          {loadMoreItems ? (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreTracker}
              threshold={chunkSize}
            >
              {({ onItemsRendered }: { onItemsRendered: any }) => (
                <List
                  outerTagName="div"
                  innerTagName="div"
                  ref={listReference}
                  height={heightAfterHead}
                  itemCount={itemCount}
                  itemSize={getSize}
                  onItemsRendered={onItemsRendered}
                  width={ruleAdjustedWidth}
                >
                  {RenderInfiniteRow}
                </List>
              )}
            </InfiniteLoader>
          ) : (
            <tbody {...getTableBodyProps()}>
              <List
                ref={listReference}
                width={ruleAdjustedWidth}
                height={heightAfterHead}
                itemCount={rows.length}
                itemSize={getSize}
              >
                {RenderRow}
              </List>
            </tbody>
          )}
        </div>
      ) : (
        <>
          {rows.length === 0 &&
            (!loading ? (
              <div>
                <Box className={classes.noDataContainer} height={heightAfterHead}>
                  <Typography variant="h6" className={classes.noData}>
                    No Data
                  </Typography>
                </Box>
              </div>
            ) : (
              <div>
                <Skeleton
                  id="loading-shimmer"
                  variant="rectangular"
                  animation="wave"
                  height={heightAfterHead}
                />
              </div>
            ))}
        </>
      )}
    </div>
  );
};
export default VirtualizedTable;
